import type { MaybeRefOrGetter } from 'vue';
import { useMutationObserver, useResizeObserver } from '@vueuse/core';
import { computed, nextTick, onMounted, onScopeDispose, toValue, watch } from 'vue';

export interface UseOverflowCoreOptions {
	containerRef: MaybeRefOrGetter<HTMLElement | null>;
	itemsRef?: MaybeRefOrGetter<HTMLElement | null>;
	itemCount: MaybeRefOrGetter<number>;
	triggerRef?: MaybeRefOrGetter<HTMLElement | null>;
	observeRef?: MaybeRefOrGetter<HTMLElement | null>;
	enabled?: MaybeRefOrGetter<boolean>;
	observeFonts?: boolean;
	recalculate: () => void;
}

/**
 * Shared deferral glue for overflow measurement: wires resize / mutation / (opt-in) font
 * observers and item-count / enabled watchers to a single `recalculate` callback, batched to one
 * call per animation frame. Deliberately holds no measurement or strategy — consumers own that.
 */
export function useOverflowCore(options: UseOverflowCoreOptions) {
	const { containerRef, itemsRef, itemCount, triggerRef, observeRef, enabled = true, observeFonts = false, recalculate } = options;

	// Batch rapid resize events into one measurement per animation frame
	let rafId: number | null = null;

	function deferRecalculate() {
		if (rafId !== null) return;

		rafId = requestAnimationFrame(() => {
			rafId = null;
			recalculate();
		});
	}

	onScopeDispose(() => {
		if (rafId !== null) cancelAnimationFrame(rafId);
	});

	useResizeObserver(computed(() => toValue(containerRef)), deferRecalculate);

	useMutationObserver(computed(() => toValue(itemsRef) ?? toValue(containerRef)), deferRecalculate, {
		childList: true,
		characterData: true,
		subtree: true,
	});

	useResizeObserver(computed(() => toValue(triggerRef)), deferRecalculate);

	if (observeRef) {
		useResizeObserver(computed(() => toValue(observeRef)), deferRecalculate);
	}

	// Item-count changes recompute from scratch on the next tick (the DOM has re-rendered by then).
	watch(() => toValue(itemCount), () => nextTick(recalculate));
	watch(() => toValue(enabled), () => deferRecalculate());

	onMounted(() => {
		nextTick(recalculate);
		// Web fonts settle their intrinsic width after mount, so re-measure once they land.
		if (observeFonts) document.fonts?.ready.then(deferRecalculate);
	});
}
